import { Box, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import { useEffect, useState } from "react";
import axiosInstance from "../../../axios/axios-instance";
import ActionButton from "../../../components/common/buttons/ActionButton";
import CancelButton from "../../../components/common/buttons/CancelButton";
import LabeledInput from "../../../components/common/fields/LabeledInput";
import { pathAPI } from "../../../utils/pathApi";
import { CancelReason, CancelReasonFormData } from "./cancelReason.types";

export interface CancelReasonDialogProps {
  open: boolean;
  /** null = Add mode, otherwise Edit mode prefilled from this row */
  reason: CancelReason | null;
  /** Language id used only when creating a new row */
  languageId: number;
  onClose: () => void;
  onSaved: (successMessage: string) => void;
  onError: (errorMessage: string) => void;
}

const EMPTY_FORM: CancelReasonFormData = { message: "", code: "" };

const CancelReasonDialog = ({
  open,
  reason,
  languageId,
  onClose,
  onSaved,
  onError,
}: CancelReasonDialogProps) => {
  const [formData, setFormData] = useState<CancelReasonFormData>(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (reason) {
      setFormData({
        message: reason.message || "",
        code: reason.code || "",
      });
    } else {
      setFormData(EMPTY_FORM);
    }
  }, [reason, open]);

  const handleChange = (field: keyof CancelReasonFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!formData.code.trim() || !formData.message.trim()) {
      onError("Please fill in all required fields");
      return;
    }
    const message = formData.message.trim();

    try {
      setSubmitting(true);
      const code = formData.code.trim().toUpperCase() || undefined;
      if (reason) {
        await axiosInstance.put(`${pathAPI.cancelReason}/${reason.id}`, {
          message,
          code,
        });
        onSaved("Cancel reason updated successfully");
      } else {
        // Server also AI-translates the new row into every other active language.
        await axiosInstance.post(pathAPI.cancelReason, {
          message,
          code,
          language: languageId,
        });
        onSaved("Cancel reason added successfully");
      }
      onClose();
    } catch (error) {
      console.error("Error saving cancel reason:", error);
      onError("Failed to save cancel reason");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => !submitting && onClose()}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        {reason ? "Edit Cancel Reason" : "Add New Cancel Reason"}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
          <LabeledInput
            label="Code"
            value={formData.code}
            onChange={(e) => handleChange("code", e.target.value)}
            placeholder="PATCH_TEST_REQUIRED"
            inputProps={{ maxLength: 64 }}
          />
          <LabeledInput
            label="Message"
            required
            multiline
            minRows={3}
            value={formData.message}
            onChange={(e) => handleChange("message", e.target.value)}
            placeholder="I changed my mind and no longer need this booking"
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <CancelButton onClick={onClose} disabled={submitting}>
          Cancel
        </CancelButton>
        <ActionButton onClick={handleSubmit} disabled={submitting}>
          {submitting ? (
            <>
              <CircularProgress size={20} sx={{ mr: 1 }} />
              Saving...
            </>
          ) : (
            "Save"
          )}
        </ActionButton>
      </DialogActions>
    </Dialog>
  );
};

export default CancelReasonDialog;
